import { Dialog, DialogContent, Divider } from '@material-ui/core';
import { Devices } from '@material-ui/icons';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Box, Button, TextBox } from '@superdispatch/ui-lab';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { chooseUTMSource } from 'shared/helpers/Analytics';
import { OrganizationIcon } from 'shared/icons/OrganizationIcon';
import { UploadCloudIcon } from 'shared/icons/UploadCloudIcon';
import { CustomerComments } from 'shared/modules/subscription/core/CustomerComments';
import { LinkButton } from 'shared/routing/Links';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { ScheduleSendIcon } from 'shared/icons/ScheduleSendIcon';
import { SpeedIcon } from 'shared/icons/SpeedIcon';
import { BackupIcon } from 'shared/icons/BackupIcon';
import { ArrowCircleLeftIcon } from 'shared/icons/ArrowCircleLeftIcon';
import { CrashedCarIcon } from 'shared/icons/CrashedCarIcon';
import { AlarmIcon } from 'shared/icons/AlarmIcon';
interface UpgradePlanDialogProps {
  open: boolean;
  onClose: () => void;
  type:
    | 'instant_transfer'
    | 'carrier_tms_pro'
    | 'load_import'
    | 'reports'
    | 'claims';
  source: string;
}

const items = [
  {
    icon: <Devices fontSize="large" htmlColor={ColorDynamic.Dark100} />,
    title: 'Advanced Load Management',
    description:
      'Effortlessly create, import, request, negotiate, and book loads all from the Web or Driver App without ever needing to make a phone call.',
  },
  {
    icon: <OrganizationIcon htmlColor={ColorDynamic.Dark100} />,
    title: 'Unified Load Organization',
    description:
      'Keep track of all your loads from any load board in one convenient place.',
  },
  {
    icon: <UploadCloudIcon htmlColor={ColorDynamic.Dark100} />,
    title: 'Boost Productivity',
    description:
      'Speed up your process by importing loads from PDF or CSV files, minimizing manual tasks and errors.',
  },
];

const reportItems = [
  {
    icon: <ScheduleSendIcon />,
    title: 'Schedule & Auto-deliver Reports',
    description:
      'Automatically send reports to any team members on your chosen cadence — daily, weekly, or monthly.',
  },
  {
    icon: <SpeedIcon />,
    title: '4 Hours Data Refresh',
    description:
      'Subscribers get data refreshed every 4 hours. Free accounts are limited to once-daily updates.',
  },
  {
    icon: <BackupIcon />,
    title: 'Full year of historical data',
    description:
      'Access historical data going back through the previous year for deeper trend analysis.',
  },
];

const claimItems = [
  {
    icon: <CrashedCarIcon />,
    title: 'Track Damage and Loss Claims',
    description:
      'Every deadline, document, and decision in one place instead of spreadsheets and email threads.',
  },
  {
    icon: <AlarmIcon />,
    title: 'Never Miss a Claim Deadline',
    description:
      'Acknowledgment and decision deadlines are calculated for you and tracked against every window, so nothing slips past its window.',
  },
  {
    icon: <ArrowCircleLeftIcon />,
    title: "Recover What You're Owed",
    description:
      'File and track claims against shippers, other carriers, and terminals — not just the ones filed against you.',
  },
];

const typeConfig = {
  instant_transfer: {
    title: 'Upgrade to Unlock Instant Transfer',
    description:
      "The Super Dispatch limited access version doesn't support Instant Transfer feature. Upgrade your plan to access Instant Transfer and everything Carrier Pro has to offer.",
  },
  carrier_tms_pro: {
    title: 'Your Trial of Carrier TMS Pro has Ended',
    description:
      'We hope you enjoyed your trial of Carrier TMS Pro. While browsing the Super Loadboard remains free, access to advanced load management features are no longer available. Upgrade to Carrier TMS Pro today to maintain full access to everything Carrier Pro has to offer.',
  },
  load_import: {
    title: 'Upgrade to Unlock Additional Features',
    description:
      "The Super Dispatch limited access version doesn't support the Create and Import Load features. Upgrade your plan to access these essential tools:",
  },
  reports: {
    title: 'Upgrade to Unlock Advanced Reporting',
    description:
      'Upgrade your plan to unlock report downloads — plus a full suite of tools that give your team complete visibility into your operations and data analysis.',
  },
  claims: {
    title: 'Upgrade to Unlock Claims',
    description:
      'Track damage and loss claims in Super Dispatch — every deadline, document, and dollar in one place instead of spreadsheets and email threads.',
  },
};

export function UpgradePlanDialog({
  open,
  onClose,
  type,
  source,
}: UpgradePlanDialogProps) {
  const { location } = window;
  const returnUrl = `${location.pathname}${location.search}`;
  const isMobile =
    useResponsiveValue('mobile', 'mobile', 'desktop') === 'mobile';
  const isWebView = MobileAppBridge.isInjected();

  const config = typeConfig[type];
  const utmSource = chooseUTMSource();
  const utmContent =
    type === 'instant_transfer'
      ? 'Instant Transfer'
      : type === 'reports'
        ? 'Advanced Reporting'
        : type === 'claims'
          ? 'Claims'
          : 'Carrier TMS Pro';

  const list =
    type === 'reports' ? reportItems : type === 'claims' ? claimItems : items;

  const trackCloseEvent = () => {
    trackLoadsEvent({
      name: 'Carrier Closed Upgrade Plan Dialog',
      utm_source: utmSource,
      utm_medium: source,
      utm_content: utmContent,
    });
  };

  return (
    <Dialog
      onClose={() => {
        trackCloseEvent();
        onClose();
      }}
      open={open}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth={true}
    >
      <DialogContent>
        <Box
          paddingBottom={isMobile ? 'xlarge' : 'medium'}
          paddingTop={isMobile ? 'small' : 'medium'}
          paddingLeft="medium"
          paddingRight="medium"
        >
          <Stack space="medium">
            <Stack space="small">
              <TextBox variant="heading-2">{config.title}</TextBox>
              <TextBox variant="body">{config.description}</TextBox>
            </Stack>

            {list.map((item) => (
              <Columns align="top" key={item.title} space="medium">
                <Column width="content">
                  <Inline horizontalAlign="right">{item.icon}</Inline>
                </Column>
                <Column>
                  <Stack space="xxsmall">
                    <TextBox variant="heading-4">{item.title}</TextBox>
                    <TextBox variant="body" color="secondary">
                      {item.description}
                    </TextBox>
                  </Stack>
                </Column>
              </Columns>
            ))}

            <Divider />

            <CustomerComments />

            <Box paddingTop="xsmall">
              <Columns space="small" collapseBelow="desktop">
                <Column width="1/2">
                  <Button
                    onClick={() => {
                      trackCloseEvent();
                      onClose();

                      if (isWebView) {
                        MobileAppBridge.closeScreen();
                      }
                    }}
                    fullWidth={true}
                    size="large"
                    variant="neutral"
                  >
                    Maybe Later
                  </Button>
                </Column>
                <Column width="1/2">
                  <LinkButton
                    to={`/settings/subscription?back_url=${encodeURIComponent(returnUrl)}&utm_medium=${source}&utm_content=${utmContent}`}
                    fullWidth={true}
                    size="large"
                    onClick={() => {
                      trackLoadsEvent({
                        name: 'Carrier Clicked Upgrade Plan',
                        utm_source: utmSource,
                        utm_medium: source,
                        utm_content: utmContent,
                      });
                    }}
                  >
                    {type === 'reports' || type === 'claims'
                      ? 'Continue to Upgrade'
                      : 'Upgrade'}
                  </LinkButton>
                </Column>
              </Columns>
            </Box>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
